import type {
  ApproveDeviceAuthorizationPayload,
  CheckDeviceAuthorizationPayload,
  CheckDeviceAuthorizationResponse,
  DenyDeviceAuthorizationPayload,
  DeviceAuthorization,
  GetDeviceAuthorizationsPayload,
  GetUserDeviceAuthorizationsPayload,
  RevokeDeviceAuthorizationPayload,
  SendApprovalRequestPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type DeviceAuthorizationsResponse = { authorizations: DeviceAuthorization[] };
type PendingDeviceRequestsResponse = { requests: DeviceAuthorization[] };

const deviceListTags = (result: DeviceAuthorization[] | undefined, listId: 'LIST' | 'PENDING') =>
  result
    ? [...result.map(({ id }) => ({ type: 'Device' as const, id })), { type: 'Device' as const, id: listId }]
    : [{ type: 'Device' as const, id: listId }];

/**
 * Mirrors `apps/server/src/modules/device` (`deviceController`). `checkDeviceAuthorization` and
 * `sendApprovalRequest` return `CheckDeviceAuthorizationResponse` as-is (its `message` field is
 * contextual UI copy explaining the auth status, e.g. "requires approval" — not a generic CRUD
 * success toast — so it's kept, unlike the stripped `message` on the mutation responses below).
 */
export const deviceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    checkDeviceAuthorization: builder.mutation<CheckDeviceAuthorizationResponse, CheckDeviceAuthorizationPayload>({
      query: (body) => ({ path: ['devices', 'check-authorization'], method: 'post', body }),
    }),

    sendApprovalRequest: builder.mutation<CheckDeviceAuthorizationResponse, SendApprovalRequestPayload>({
      query: (body) => ({ path: ['devices', 'send-approval-request'], method: 'post', body }),
      invalidatesTags: [{ type: 'Device', id: 'PENDING' }],
    }),

    getPendingDeviceRequests: builder.query<DeviceAuthorization[], void>({
      query: () => ({ path: ['devices', 'pending-requests'], method: 'get' }),
      transformResponse: (response: PendingDeviceRequestsResponse) => response.requests,
      providesTags: (result) => deviceListTags(result, 'PENDING'),
    }),

    getDeviceAuthorizations: builder.query<DeviceAuthorization[], GetDeviceAuthorizationsPayload | undefined>({
      query: (payload) => ({ path: ['devices', 'authorizations'], method: 'get', query: { status: payload?.status } }),
      transformResponse: (response: DeviceAuthorizationsResponse) => response.authorizations,
      providesTags: (result) => deviceListTags(result, 'LIST'),
    }),

    getUserDeviceAuthorizations: builder.query<DeviceAuthorization[], GetUserDeviceAuthorizationsPayload>({
      query: ({ user_id, include_revoked }) => ({
        path: ['devices', 'user', { param: 'user_id', value: user_id }, 'authorizations'],
        method: 'get',
        // The server route also validates `user_id` as a query param even though it's already
        // in the path — replicate exactly, see apps/server/src/modules/device/index.ts.
        query: { user_id, include_revoked: include_revoked ?? false },
      }),
      transformResponse: (response: DeviceAuthorizationsResponse) => response.authorizations,
      providesTags: (result, _error, { user_id }) => [
        ...deviceListTags(result, 'LIST'),
        { type: 'Device', id: `USER_${user_id}` },
      ],
    }),

    approveDeviceAuthorization: builder.mutation<void, ApproveDeviceAuthorizationPayload>({
      query: ({ device_authorization_id }) => ({
        path: ['devices', { param: 'device_authorization_id', value: device_authorization_id }, 'approve'],
        method: 'post',
      }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, { device_authorization_id }) => [
        { type: 'Device', id: device_authorization_id },
        { type: 'Device', id: 'LIST' },
        { type: 'Device', id: 'PENDING' },
      ],
    }),

    denyDeviceAuthorization: builder.mutation<void, DenyDeviceAuthorizationPayload>({
      query: ({ device_authorization_id }) => ({
        path: ['devices', { param: 'device_authorization_id', value: device_authorization_id }, 'deny'],
        method: 'delete',
      }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, { device_authorization_id }) => [
        { type: 'Device', id: device_authorization_id },
        { type: 'Device', id: 'PENDING' },
      ],
    }),

    revokeDeviceAuthorization: builder.mutation<void, RevokeDeviceAuthorizationPayload>({
      query: ({ device_authorization_id }) => ({
        path: ['devices', { param: 'device_authorization_id', value: device_authorization_id }, 'revoke'],
        method: 'delete',
      }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, { device_authorization_id }) => [
        { type: 'Device', id: device_authorization_id },
        { type: 'Device', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useCheckDeviceAuthorizationMutation,
  useSendApprovalRequestMutation,
  useGetPendingDeviceRequestsQuery,
  useGetDeviceAuthorizationsQuery,
  useGetUserDeviceAuthorizationsQuery,
  useApproveDeviceAuthorizationMutation,
  useDenyDeviceAuthorizationMutation,
  useRevokeDeviceAuthorizationMutation,
} = deviceApi;
